/**
 * [PURPOSE]: Data-driven racial resource definitions (parallel to classResources.js).
 * [WHY]: Several SRD races have once-per-rest powers — Half-Orc Relentless
 *   Endurance, Aasimar Healing Hands, Tiefling Infernal Legacy spells, Drow
 *   Magic spells, Dragonborn Breath Weapon, etc. Phase 4b registered most
 *   of these as `feature` flags but lacked a resource counter. This file
 *   provides the schema; ClassResourcesPanel + RestPanel surface the
 *   resources alongside class resources.
 *
 * [INTEGRATION]:
 *   - getResourcesForRace(raceName, subraceName) returns the list of
 *     resource definitions applicable to a character's race + subrace.
 *   - Storage: playerStats.raceResources = { <id>: { current, max } }.
 *   - RestPanel calls rechargesOnRest() per resource on rest events and
 *     resets counters.
 *
 * Resource schema (mirrors classResources.js to share calculator helpers):
 *   id:             string  — Storage key (e.g., 'relentless_endurance')
 *   name:           string  — Display name
 *   icon:           string  — Emoji icon
 *   rechargeOn:     'short_rest' | 'long_rest'
 *   maxCalculation: 'fixed:<N>' | 'classLevel' | 'classLevel*N'
 *                   — Reuses classResources.calculateResourceMax for the same
 *                     formula vocabulary; here `classLevel` means the
 *                     character's TOTAL level (no class context for races).
 *   color:          string  — UI color theme
 *   description:    string  — Tooltip
 *   subrace?:       string  — Only applies to a specific subrace
 *
 * [Phase 4b combat-panel-UI followup — APR 2026]: Closes the racial-once-
 *   per-rest gap deferred from Bucket 4 race-trait wiring.
 */

export const RACE_RESOURCE_DEFINITIONS = {
  'Half-Orc': [
    {
      id: 'relentless_endurance',
      name: 'Relentless Endurance',
      icon: '💪', // 💪
      rechargeOn: 'long_rest',
      maxCalculation: 'fixed:1',
      color: 'red',
      description: "When reduced to 0 HP and not killed outright, drop to 1 HP instead. PHB p.41."
    }
  ],

  Aasimar: [
    {
      // Aasimar Healing Hands (Volo's Guide / MOM)
      id: 'healing_hands',
      name: 'Healing Hands',
      icon: '🙏', // 🙏
      rechargeOn: 'long_rest',
      maxCalculation: 'fixed:1',
      color: 'gold',
      description: 'Action: touch a creature, restore your level d4 HP. 1/long rest. VGtM p.105 / MOM.'
    }
  ],

  Tiefling: [
    {
      // Tiefling Infernal Legacy: Thaumaturgy at-will (no tracker), Hellish
      // Rebuke at L3 (1/long rest), Darkness at L5 (1/long rest).
      id: 'infernal_legacy_hellish_rebuke',
      name: 'Hellish Rebuke',
      icon: '🔥', // 🔥
      rechargeOn: 'long_rest',
      minLevel: 3,
      maxCalculation: 'fixed:1',
      color: 'red',
      description: 'Reaction (when damaged): cast Hellish Rebuke as a 2nd-level spell, no slot needed. 1/long rest. PHB p.43.',
      // [Sub-plan A2-classres-consumers — 2026-05-30]: Property flag triggers
      // "Use Reaction" button in ClassResourcesPanel — data-driven (Rule 11).
      useAbilityModal: true,
    },
    {
      id: 'infernal_legacy_darkness',
      name: 'Darkness',
      icon: '🌑', // 🌑
      rechargeOn: 'long_rest',
      minLevel: 5,
      maxCalculation: 'fixed:1',
      color: 'purple',
      description: 'Cast Darkness once per long rest, no slot needed. PHB p.43.'
    }
  ],

  Dragonborn: [
    {
      id: 'breath_weapon',
      name: 'Breath Weapon',
      icon: '🐲', // 🐲
      // [PHB 2014]: 1/short rest. [PHB 2024 / TCE]: also 1/short rest. Either way
      // the resource recharges on short or long rest.
      rechargeOn: 'short_rest',
      maxCalculation: 'fixed:1',
      color: 'orange',
      description: 'Exhale destructive energy: damage type and shape based on draconic ancestry. 1/short rest. PHB p.34.',
      // [Sub-plan A2-classres-consumers — 2026-05-30]: Property flag triggers
      // "Use" button in ClassResourcesPanel — data-driven (Rule 11).
      // [BREADCRUMB — generalization]: useAbilityModal:true is a D&D-specific
      //   flag pattern. The endgame generic replacement is a first-class
      //   "usableAction" schema on the resource def that carries rollSpec +
      //   dcFormula inline (removing ClassResourcesPanel's per-ID handler).
      //   Tracked at master-queue.md Sub-plan A2-gen.
      useAbilityModal: true,
    }
  ],

  Elf: [
    // Drow Magic only (Dark Elf / Drow subrace) — Faerie Fire at L3,
    // Darkness at L5, both 1/long rest. Other Elf subraces have no
    // tracked racial resources beyond the always-on traits.
    {
      id: 'drow_magic_faerie_fire',
      name: 'Faerie Fire',
      icon: '✨', // ✨
      rechargeOn: 'long_rest',
      minLevel: 3,
      maxCalculation: 'fixed:1',
      color: 'pink',
      subrace: 'Dark Elf (Drow)',
      description: 'Cast Faerie Fire once per long rest, no slot needed. PHB p.24 (Drow Magic).'
    },
    {
      id: 'drow_magic_darkness',
      name: 'Darkness (Drow)',
      icon: '🌑', // 🌑
      rechargeOn: 'long_rest',
      minLevel: 5,
      maxCalculation: 'fixed:1',
      color: 'purple',
      subrace: 'Dark Elf (Drow)',
      description: 'Cast Darkness once per long rest, no slot needed. PHB p.24 (Drow Magic).'
    }
  ],

  Gnome: [
    // Forest Gnome Minor Illusion at-will (no tracker). Rock Gnome Tinker
    // creates clockwork toys (no tracker — flavor). No racial resources to
    // track for Gnome.
  ],

  Halfling: [
    // Halfling Lucky reroll-on-1 isn't a counter (always-available). No
    // racial resource needed.
  ],

  // Dwarves — no per-rest racial powers; all traits are passive.
  // Humans — no racial resources.
  // Half-Elves — no racial resources beyond passive traits.
};

/**
 * Returns the array of racial resource definitions applicable to a
 * character's race + (optional) subrace.
 *
 * @param {string} raceName  — e.g., 'Tiefling', 'Half-Orc'
 * @param {string|null} subraceName — e.g., 'Dark Elf (Drow)'; null for races
 *                                    without subrace-specific resources
 * @returns {Array<object>}
 */
export function getResourcesForRace(raceName, subraceName = null) {
  if (!raceName) return [];
  const raw = RACE_RESOURCE_DEFINITIONS[raceName] || [];
  if (!subraceName) {
    // Filter out subrace-specific entries when no subrace is set
    return raw.filter(r => !r.subrace);
  }
  // Include both base-race resources and any matching subrace-specific ones
  return raw.filter(r => !r.subrace || r.subrace === subraceName);
}
